import { html, type TemplateResult } from 'lit';
import { replaceTerms } from './lang';
import { terms } from './terms';
import { personName } from '../utility/person-name';
import { userDirectoryInstance } from '../global-data/user-directory-instance';
import { splitPersonTags } from './person-tag';
import '../components/user/username';

/**
 * Text the server keeps - an audit description, a change's before and after - turned into what a
 * reader sees (contact rule 8, CONTACT-010).
 *
 * Kept text carries two kinds of tag, and both are resolved here, at render time, so a stored row
 * is never rewritten and still reads right after a rename (D-027):
 * - `{{term key.variant}}` names a domain concept, resolved as `replaceTerms` does;
 * - `{{person <login user id>}}` names a person (server `PersonTag.For`), shown as `<user-name>`,
 *   their current display name with their card, never their username.
 *
 * A person tag whose id is not an id renders exactly as a missing term does - the visible
 * MISSING TERM marker and the console report - and never throws: a stored row that is wrong must
 * degrade that row, not take the page down (D-028).
 */

/** Kept text as a template: terms resolved, each person as their name with their card. */
export function renderKeptText(text: string): TemplateResult {
  return html`${splitPersonTags(text).map((piece) =>
    'personId' in piece
      ? html`<user-name .userId=${piece.personId}></user-name>`
      : replaceTerms(piece.text)
  )}`;
}

/**
 * Kept text that is markdown (an audit note behind the `@#MD` marker), made ready for the markdown
 * renderer: terms resolved, and each person tag turned into a `<user-name>` element as inline HTML,
 * which the renderer passes through when told the text is kept (`keptPeople`), so the person survives the markdown rendering as their name
 * with their card rather than as the raw tag. Safe to emit as HTML because only a tag whose id
 * matched the login-user-id shape becomes an element; anything else stays text.
 */
export function keptTextMarkdown(text: string): string {
  return splitPersonTags(text)
    .map((piece) =>
      'personId' in piece
        ? `<user-name userid="${piece.personId}"></user-name>`
        : replaceTerms(piece.text)
    )
    .join('');
}

/**
 * Kept text as plain text, for a place that cannot hold an element (a title attribute; a toast
 * holds one, and renders a KeptText with renderKeptText):
 * terms resolved, and each person as their display name from the directory cache, or "a <user>"
 * when the cache does not hold them yet. Never the id or the tag.
 */
export function keptTextPlain(text: string): string {
  return splitPersonTags(text)
    .map((piece) => {
      if (!('personId' in piece)) return replaceTerms(piece.text);
      const person = userDirectoryInstance().getUserDtoSync(piece.personId);
      return person
        ? personName(person.displayName)
        : `a ${terms.loginUser.lower}`;
    })
    .join('');
}
